import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineCamera } from "react-icons/ai"; // Camera icon
import { FiPaperclip } from "react-icons/fi"; // Paperclip icon
import { Button, Modal, ModalBody } from "reactstrap";
import { useDeviceType } from "../../../Hooks/useDeviceType";

const FileUploadInput = ({
  theme,
  selectedFiles = [],
  handleFileChange,
  inputIdCamera = "file-camera",
  inputIdFile = "file-upload",
  cameraLabel = "Take a picture",
  fileLabel = "Upload a file",
}) => {
  const { t } = useTranslation();
  const deviceType = useDeviceType();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null); // Store camera stream for cleanup

  // Base styles
  const baseStyle = {
    background: theme === "dark-only" ? "#1d1e26" : "#fff",
    border: theme === "dark-only" ? "2px dashed #666666" : "2px dashed #cacaca",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px",
    cursor: "pointer",
    textAlign: "center",
    gap: "10px",
    flexDirection: "column",
    width: "100%",
  };

  const isMobile = deviceType === "mobile";

  // Toggle modal
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      startCamera();
    } else {
      stopCamera();
    }
  };

  // Start the camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: "environment" } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      alert("Camera error: " + err.message);
    }
  };
  
  // const startCamera = async () => {
  //   try {
  //     const stream = await navigator.mediaDevices.getUserMedia({ video: true });
  //     streamRef.current = stream;
  //     if (videoRef.current) {
  //       videoRef.current.srcObject = stream;
  //     }
  //   } catch (err) {
  //     console.error("Camera error:", err);
  //     alert("Camera error: " + err.message);
  //   }
  // };

  // Stop the camera
  const stopCamera = () => {
    const stream = streamRef.current;
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  // Generate timestamp
  const generateTimestamp = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");

    return `${year}-${month}-${day}_${hours}:${minutes}:${seconds}`;
  };

  // Capture photo and close modal
  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/png");
    setPhoto(dataUrl);

    // Generate the filename with timestamp
    const timestamp = generateTimestamp();
    const filename = `camera_${timestamp}.png`;

    // Create a new File from the data URL
    fetch(dataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], filename, { type: blob.type });

        // Mimic the file input change event to add the photo to selected files
        handleFileChange({ target: { files: [file] } });

        // Close modal and stop camera
        setIsModalOpen(false);
        stopCamera();
      });
  };

  return (
    <div>
      {/* File Upload Input */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr",
          gap: "20px",
          width: "100%",
        }}
      >
        {/* Camera Input */}
        {isMobile && (
          <label
            htmlFor={inputIdCamera}
            style={baseStyle}
            onClick={toggleModal}
          >
            <AiOutlineCamera size={48} /> {/* Camera icon from react-icons */}
            <div>{t(cameraLabel)}</div>
          </label>
        )}

        {/* File Upload Input */}
        <label htmlFor={inputIdFile} style={baseStyle}>
          <FiPaperclip size={48} /> {/* Paperclip icon from react-icons */}
          <div>{t(fileLabel)}</div>
          <input
            type="file"
            id={inputIdFile}
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </label>

        {/* Selected Files Info */}
        {selectedFiles.length > 0 && (
          <div
            style={{ gridColumn: "1 / -1", textAlign: "center", marginTop: 10 }}
          >
            <strong>{selectedFiles.length}</strong>{" "}
            {selectedFiles.length === 1 ? "File" : "Files"} Selected
          </div>
        )}
      </div>

      {/* Camera Modal */}
      <Modal
        isOpen={isModalOpen}
        toggle={toggleModal}
        size={"lg"}
        centered
        backdrop="static"
        keyboard={false}
      >
        <button
          onClick={toggleModal}
          className="remove-button"
          style={{
            width: isMobile ? "30px" : "40px",
            height: isMobile ? "30px" : "40px",
          }}
          title="Remove file"
        >
          <i className="fa fa-close"></i>
        </button>
        <ModalBody
          style={{
            maxHeight: "82vh",
            overflowY: "auto", // Add vertical scrolling
            //paddingRight: "15px", // Add some padding to prevent scrollbar overlap
            //marginBottom: 20,
            padding: 8,
            display: "flex", // Enable flexbox
            flexDirection: "column", // Align items vertically
            justifyContent: "center", // Center content vertically
            alignItems: "center", // Center content horizontally
          }}
        >
          <div style={{ textAlign: "center", width: "100%" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              width="100%"
              height="100%"
              style={{ objectFit: "cover", borderRadius: 5 }}
            />
            <br />
            {/* Center the Camera icon below the video */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Button
                color="secondary"
                onClick={takePhoto}
                style={{
                  fontSize: "30px", // Size of the icon
                  width: "60px", // Icon width
                  height: "60px", // Icon height
                  borderRadius: "50%", // Circular icon
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center", // Center the icon inside the button
                  marginTop: "20px", // Space between the video and icon
                }}
                className="mt-3"
              >
                <AiOutlineCamera size={30} /> {/* Camera icon */}
              </Button>
            </div>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
          </div>
        </ModalBody>
      </Modal>

      {/* <Modal
        isOpen={isModalOpen}
        toggle={toggleModal}
        size="lg" // Removed 'fullscreen', now we will enforce full-screen manually.
        style={{
          maxWidth: "100%",
          width: "100%",
          height: "100vh", // Full screen height
          padding: 0,
          margin: 0,
        }}
      >
        <ModalHeader toggle={toggleModal} style={{ display: "none" }}>
          Camera Capture
        </ModalHeader>
        <ModalBody
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%", // Full height
            padding: 0,
            backgroundColor: "black", // Optional, can add a black background to the video
          }}
        >
          <div style={{ textAlign: "center", width: "100%" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              width="100%"
              height="100%"
              style={{ objectFit: "cover" }}
            />
            <br />
            <Button color="secondary" onClick={takePhoto} className="mt-3">
              Take Photo
            </Button>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
          </div>
        </ModalBody>
      </Modal> */}
    </div>
  );
};

export default FileUploadInput;
